import { Button } from '@finn/design-system/atoms/button';
import { Input } from '@finn/design-system/atoms/input';
import { Spinner } from '@finn/design-system/atoms/spinner';
import { CarSearchOutlined } from '@finn/design-system/icons/car-search-outlined';
import { Features, useGetFeature } from '@finn/ua-featureflags';
import { TrackingEventName } from '@finn/ua-tracking';
import {
  cn,
  getCloudinaryImgUrl,
  getCloudinarySrcSet,
  trackEvent,
} from '@finn/ui-utils';
import { useRouter } from 'next/navigation';
import React, { useRef, useState } from 'react';

import HeroFilters from './HeroFilters';
import { Hero2CosmicMetadata } from './metaData';

type Props = {
  data: Hero2CosmicMetadata;
  title: string;
  imgURL: string;
};

type AiResponse = {
  url: string;
  total_results: number;
  not_found?: boolean;
  wrong_prompt?: boolean;
  reason?: string;
};

export const HeroWithFilters: React.FC<Props> = ({ data, title, imgURL }) => {
  const [prompt, setPrompt] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isExecutingAi, setIsExecutingAi] = useState(false);
  const aiVariant = useGetFeature(Features.ExpAiSearch);
  const isSearchExp = aiVariant === 'b' || aiVariant === 'c';
  const isBVariant = aiVariant === 'b';
  const isCVariant = aiVariant === 'c';
  const { push } = useRouter();

  const handleSearch = async () => {
    inputRef.current?.blur();
    setIsExecutingAi(true);

    if (!prompt) {
      setTimeout(() => {
        push('/subscribe');
      }, 100);

      return;
    }

    trackEvent(TrackingEventName.SEARCH_SUBMITTED, {
      query: prompt,
    });

    setTimeout(async () => {
      try {
        const response = await fetch('/api/ai-search', {
          method: 'POST',
          body: JSON.stringify({ message: prompt }),
        });
        const { url, total_results, not_found, wrong_prompt, reason } =
          (await response.json()) as AiResponse;

        trackEvent(TrackingEventName.SEARCH_RESULT, {
          query: prompt,
          total_results,
          not_found,
          wrong_prompt,
          reason,
        });

        const reasonUrl = `${url?.includes('?') ? '&' : '?'}reason=${reason ? encodeURIComponent(reason) : ''}`;

        if (url) {
          push(`${url}${reasonUrl}&reason_type=success`);

          return;
        }

        if (not_found) {
          push(`/subscribe?reason_type=not_found`);

          return;
        }
        if (wrong_prompt) {
          push(`/subscribe${reasonUrl}&reason_type=wrong_prompt`);

          return;
        }
        if (total_results === 0) {
          push(`/subscribe?reason_type=no_results`);
        }
      } catch (error) {
        console.error(error);
        setIsExecutingAi(false);
      }
    });
  };

  if (!data) return null;

  const srcSet = getCloudinarySrcSet(imgURL, [480, 800, 1200, 1600]);
  const src = getCloudinaryImgUrl(imgURL, { w: 1200, dpr: 2 });

  return (
    <div
      className={
        'relative h-[622px] bg-gradient-to-b from-black/[8%] to-black/0 md:h-[732px]'
      }
    >
      <img
        draggable={false}
        src={src}
        srcSet={srcSet}
        alt="Hero"
        className={`-z-overlay absolute inset-0 h-full w-full object-cover`}
      />
      <div className="container" data-testid="hero2">
        <div
          className={cn('-mb-10 grid gap-4 md:-mb-24', {
            'sm:grid-cols-[400px_1fr] md:grid-cols-[500px_1fr]': !isSearchExp,
            'sm:grid-cols-[600px_1fr] sm:pt-8 md:grid-cols-[750px_1fr]':
              isBVariant,
            'sm:grid-cols-[600px_1fr] sm:pt-8 md:grid-cols-[600px_1fr]':
              isCVariant,
          })}
        >
          <div
            className={cn('w-full', {
              'mt-[124px]': !isSearchExp,
              'mt-[290px] sm:mt-[260px]': isSearchExp,
            })}
          >
            <h1
              className={cn(
                'mobile-t2-semibold md:web-t2-semibold mb-6 text-left text-white',
                {
                  'sm:pr-40': isBVariant,
                }
              )}
              data-cy="hero-title"
            >
              {isSearchExp ? 'Welches Auto möchtest du fahren?' : title}
            </h1>

            {isSearchExp ? (
              <form
                action={handleSearch}
                className="relative flex flex-col gap-2 sm:flex-row"
              >
                {isExecutingAi && (
                  <div
                    className={cn(
                      'z-overlay border-trustedBlue shadow-trustedBlue absolute left-0 top-0 h-14 w-full animate-pulse border-2 shadow-lg',
                      {
                        'sm:w-[calc(100%-168px)]': isBVariant,
                      }
                    )}
                  />
                )}
                <div className="flex-1">
                  <Input
                    className={'[&_input]:placeholder-opacity-100'}
                    startAdornment={<CarSearchOutlined />}
                    ref={inputRef}
                    placeholder="Ein Familienauto unter 700 Euro"
                    maxLength={200}
                    value={prompt}
                    onSubmit={handleSearch}
                    endAdornment={
                      isExecutingAi && (
                        <Spinner className="fill-trustedBluePressed h-8 w-8 animate-spin" />
                      )
                    }
                    onChange={(e) => {
                      setPrompt(e.target.value);
                    }}
                  />
                </div>
                {!isCVariant && (
                  <Button
                    type="submit"
                    className="w-full sm:w-40"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      trackEvent(TrackingEventName.CTA_CLICKED, {
                        location: 'Homepage Hero',
                        link: '/subscribe',
                      });

                      handleSearch();
                    }}
                  >
                    Auto finden
                  </Button>
                )}
              </form>
            ) : (
              <HeroFilters primaryCTA={data.primary_cta} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
